import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, Button, Card, Field, Input, PageHeader, ReadOnlyText, Select, Spinner, StatusPill, Textarea } from '../components/ui';
import { UploadDropzone } from '../components/UploadDropzone';
import { OduCollectionPicker } from '../components/OduCollectionPicker';
import { deriveDescription } from '../utils/format';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import { MediaAPI } from '../services/endpoints/media';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { Media, Transcript, PurchaseOption } from '../types/api';
import { PURCHASE_OPTIONS, DEFAULT_PURCHASE_OPTION } from '../data/media';
import './MediaDetailPage.css';

type LangKey = 'en' | 'yo' | 'pt' | 'es' | 'fr';

interface TranscriptLang { key: keyof Transcript; lang: LangKey; label: string; sub: string; }
const TRANSCRIPT_LANGS: TranscriptLang[] = [
  { key: 'text',   lang: 'yo', label: 'Yoruba',     sub: 'text · editable' },
  { key: 'textEn', lang: 'en', label: 'English',    sub: 'textEn · editable' },
  { key: 'textPt', lang: 'pt', label: 'Portuguese', sub: 'textPt · read only' },
  { key: 'textEs', lang: 'es', label: 'Spanish',    sub: 'textEs · read only' },
  { key: 'textFr', lang: 'fr', label: 'French',     sub: 'textFr · read only' },
];

type LongDescKey = 'longDescription' | 'longDescriptionPt' | 'longDescriptionEs' | 'longDescriptionFr';
interface LongDescLang { key: LongDescKey; lang: LangKey; label: string; sub: string; }
const LONG_DESC_LANGS: LongDescLang[] = [
  { key: 'longDescription',   lang: 'en', label: 'English',    sub: 'longDescription · editable' },
  { key: 'longDescriptionPt', lang: 'pt', label: 'Portuguese', sub: 'longDescriptionPt · read only' },
  { key: 'longDescriptionEs', lang: 'es', label: 'Spanish',    sub: 'longDescriptionEs · read only' },
  { key: 'longDescriptionFr', lang: 'fr', label: 'French',     sub: 'longDescriptionFr · read only' },
];

export function MediaDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [media, setMedia] = useState<Media | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Partial<Media>>({});
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const { run } = useDispatchAction();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const m = await MediaAPI.get(id);
        if (!alive) return;
        setMedia(m);
        setDraft(m);

        // Transcript + long descriptions are @JsonIgnore'd on /media/get. Load them
        // from the admin-only plaintext endpoint so every section preloads for editing,
        // for any status (drafts included) and with no subscription required.
        setContentLoading(true);
        setContentError(null);
        try {
          const content = await MediaAPI.content(id);
          if (!alive) return;
          const merge = (prev: Partial<Media>): Partial<Media> => ({
            ...prev,
            transcript: { ...(prev.transcript || {}), ...(content.transcript || {}) },
            longDescription:   content.longDescription   ?? prev.longDescription,
            longDescriptionPt: content.longDescriptionPt ?? prev.longDescriptionPt,
            longDescriptionEs: content.longDescriptionEs ?? prev.longDescriptionEs,
            longDescriptionFr: content.longDescriptionFr ?? prev.longDescriptionFr,
          });
          // Merge into both so a section Reset (setDraft(media)) keeps loaded content.
          setMedia(mm => (mm ? (merge(mm) as Media) : mm));
          setDraft(merge);
        } catch (err) {
          if (!alive) return;
          setContentError((err as Error).message || 'Could not load content');
        } finally {
          if (alive) setContentLoading(false);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [id]);

  // Each form section saves independently: send only that section's fields + id. The
  // backend /media/save merges the patch over the stored record (null = keep existing),
  // so saving one section never nulls the others.
  async function saveSection(label: string, patch: Partial<Media>) {
    setSaving(true);
    const result = await run(label, () => MediaAPI.save({ id, ...patch }));
    if (result) {
      // /media/save returns MediaResponse which strips the @JsonIgnore'd content fields.
      // Re-apply the locally-held content so the editor keeps showing it.
      const content = {
        transcript: draft.transcript,
        longDescription:   draft.longDescription,
        longDescriptionPt: draft.longDescriptionPt,
        longDescriptionEs: draft.longDescriptionEs,
        longDescriptionFr: draft.longDescriptionFr,
      };
      setMedia({ ...result, ...content });
      setDraft({ ...result, ...content });
    }
    setSaving(false);
  }

  // Short description is always derived from the Yoruba transcript, never typed.
  const saveDetails = () => saveSection('Save details', {
    title: draft.title,
    subtitle: draft.subtitle,
    author: draft.author,
    collectionName: draft.collectionName,
    purchaseOption: draft.purchaseOption,
    description: deriveDescription(draft.transcript?.text),
  });

  // Only English is editable; Pt/Es/Fr are server-generated translations (preserved by merge).
  const saveDescriptions = () => saveSection('Save descriptions', {
    longDescription: draft.longDescription,
  });

  // Only Yoruba + English are editable; Pt/Es/Fr translations are preserved by the merge.
  const saveTranscripts = () => saveSection('Save transcripts', {
    transcript: { text: draft.transcript?.text, textEn: draft.transcript?.textEn },
    description: deriveDescription(draft.transcript?.text),
  });

  async function togglePublish() {
    if (!media) return;
    const next = media.status !== 'PUBLISHED';
    // Distinct endpoints — both return 200/empty, so re-fetch the canonical record
    // afterwards (this also picks up the releaseDate the backend stamps on publish).
    const ok = await run(next ? 'Publish' : 'Unpublish', () =>
      next ? MediaAPI.publish(id) : MediaAPI.unpublish(id));
    if (ok !== null) {
      const fresh = await MediaAPI.get(id);
      // /media/get omits content — carry the loaded content so a later Reset keeps it.
      setMedia(m => ({
        ...fresh,
        transcript: m?.transcript,
        longDescription:   m?.longDescription,
        longDescriptionPt: m?.longDescriptionPt,
        longDescriptionEs: m?.longDescriptionEs,
        longDescriptionFr: m?.longDescriptionFr,
      }));
      // Patch only the publish-affected fields so unsaved transcript / long-description
      // edits in the draft aren't clobbered (those never come back from /media/get).
      setDraft(d => ({
        ...d,
        status: fresh.status,
        draft: fresh.draft,
        releaseDate: fresh.releaseDate,
        publishTimestamp: fresh.publishTimestamp,
        pinned: fresh.pinned,
        featured: fresh.featured,
      }));
    }
  }

  async function togglePin() {
    if (!media) return;
    const next = !media.pinned;
    // /media/feature returns only { id }, so update the pin/featured flags locally.
    const result = await run(next ? 'Pin as featured' : 'Unpin featured', () => MediaAPI.feature(id, next));
    if (result) {
      setMedia(m => (m ? { ...m, pinned: next, featured: next } : m));
      setDraft(d => ({ ...d, pinned: next, featured: next }));
    }
  }

  async function doDelete(_reason?: string) {
    // run() returns null only on failure (void success resolves to undefined).
    const result = await run('Delete media', () => MediaAPI.delete(id));
    if (result === null) throw new Error('delete failed'); // keep the confirm dialog open
    nav(media?.type === 'VIDEO' ? '/videos' : '/media');
  }

  if (loading) return <Card><Spinner /> Loading…</Card>;
  if (!media) return <Card>Media not found.</Card>;

  return (
    <div>
      <PageHeader
        eyebrow={`02 — MEDIA · ${media.id.slice(0, 8)}`}
        title={<>{draft.title || <em>(untitled)</em>}</>}
        lede={<>Status: <MediaStatusPill status={media.status} /> {media.pinned ? <>&nbsp;·&nbsp; <StatusPill tone="ember">Pinned featured</StatusPill></> : media.featured ? <>&nbsp;·&nbsp; <StatusPill tone="moss">Featured today</StatusPill></> : null} &nbsp;·&nbsp; Type: {media.type}{media.collectionName ? ` · ${media.collectionName}` : ''}{media.author ? ` · by ${media.author}` : ''}</>}
        actions={
          <>
            <Button onClick={() => nav(media.type === 'VIDEO' ? '/videos' : '/media')}>← Back</Button>
            <Button onClick={togglePublish} tone={media.status === 'PUBLISHED' ? 'ghost' : 'ember'}>
              {media.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
            </Button>
            <Button
              onClick={togglePin}
              tone={media.pinned ? 'ink' : 'ghost'}
              disabled={media.status !== 'PUBLISHED'}
              title={media.status !== 'PUBLISHED' ? 'Only published media can be featured' : undefined}
            >
              {media.pinned ? 'Unpin (resume auto-rotation)' : 'Pin as featured'}
            </Button>
            <Button onClick={() => setDeleting(true)} tone="danger">Delete</Button>
          </>
        }
      />

      <div className="md-grid">
        <div className="md-col">
          {/* ── Editable details ───────────────────────────────────── */}
          <Card>
            <h2 className="md-h">Details</h2>

            <dl className="md-static">
              <Row label="Type"             value={media.type || '—'} />
              <Row label="Release date"     value={media.releaseDate || '—'} />
            </dl>

            <div className="md-stack">
              <div>
                <div className="nb-field__label" style={{ marginBottom: 6 }}>Collection name</div>
                <OduCollectionPicker
                  value={draft.collectionName}
                  onChange={cn => setDraft({ ...draft, collectionName: cn })}
                />
              </div>
              <Field label="Title">
                <Input value={draft.title || ''} onChange={e => setDraft({ ...draft, title: e.target.value })} />
              </Field>
              <Field label="Subtitle">
                <Input value={draft.subtitle || ''} onChange={e => setDraft({ ...draft, subtitle: e.target.value })} />
              </Field>
              <Field label="Author">
                <Input value={draft.author || ''} onChange={e => setDraft({ ...draft, author: e.target.value })} placeholder="Author name" />
              </Field>
              <Field
                label="Access"
                hint={PURCHASE_OPTIONS.find(o => o.value === (draft.purchaseOption ?? DEFAULT_PURCHASE_OPTION))?.hint}
              >
                <Select
                  value={draft.purchaseOption ?? DEFAULT_PURCHASE_OPTION}
                  onChange={e => setDraft({ ...draft, purchaseOption: e.target.value as PurchaseOption })}
                >
                  {PURCHASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </Field>
              <div>
                <div className="nb-field__label" style={{ marginBottom: 6 }}>Description</div>
                <ReadOnlyText
                  value={deriveDescription(draft.transcript?.text) || media.description}
                  placeholder="Auto-derived from the Yoruba transcript once it's added."
                />
                <span className="nb-field__hint" style={{ marginTop: 6, display: 'block' }}>
                  Short summary shown in lists and on the dashboard. Derived automatically from the Yoruba transcript.
                </span>
              </div>
              <div className="md-actions">
                <Button onClick={() => setDraft(media)} tone="ghost">Reset</Button>
                <Button onClick={saveDetails} tone="ember" disabled={saving}>
                  {saving ? 'Saving…' : 'Save details'}
                </Button>
              </div>
            </div>
          </Card>

          {/* ── Long descriptions ──────────────────────────────────── */}
          <Card>
            <header className="md-section-head">
              <h2 className="md-h">Long descriptions</h2>
              <ContentBadge loading={contentLoading} error={contentError} />
            </header>

            {contentError && (
              <div className="md-error">
                Could not load saved content. {contentError && <code>{contentError}</code>}
              </div>
            )}

            <p className="md-section-note">
              Only the English version is editable here. Translations are imported separately and shown read-only.
            </p>

            <div className="md-acc-list">
              {LONG_DESC_LANGS.map(({ key, lang, label, sub }) => {
                const value = (draft[key] as string | undefined) || '';
                const isEnglish = lang === 'en';
                return (
                  <Accordion
                    key={key}
                    title={label}
                    subtitle={sub}
                    defaultOpen={isEnglish}
                    badge={
                      isEnglish
                        ? <StatusPill tone="ember">Editable</StatusPill>
                        : value
                          ? <StatusPill tone="brass">Read only</StatusPill>
                          : <StatusPill tone="moss">Empty</StatusPill>
                    }
                  >
                    {isEnglish ? (
                      <Textarea
                        value={value}
                        onChange={e => setDraft({ ...draft, [key]: e.target.value })}
                        rows={10}
                        disabled={contentLoading}
                        placeholder={contentLoading ? 'Loading…' : ''}
                      />
                    ) : (
                      <ReadOnlyText value={value} placeholder="No translation imported yet." />
                    )}
                  </Accordion>
                );
              })}
            </div>

            <div className="md-actions">
              <Button onClick={saveDescriptions} tone="ember" disabled={saving || contentLoading}>
                {saving ? 'Saving…' : 'Save descriptions'}
              </Button>
            </div>
          </Card>

          {/* ── Transcripts ────────────────────────────────────────── */}
          <Card>
            <header className="md-section-head">
              <h2 className="md-h">Transcripts</h2>
              <ContentBadge loading={contentLoading} error={contentError} />
            </header>

            {contentError && (
              <div className="md-error">
                Could not load saved content. {contentError && <code>{contentError}</code>}
              </div>
            )}

            <p className="md-section-note">
              The Yoruba and English transcripts are editable here. Portuguese, Spanish, and French come from the translation pipeline and are shown read-only.
            </p>

            <div className="md-acc-list">
              {TRANSCRIPT_LANGS.map(({ key, lang, label, sub }) => {
                const value = draft.transcript?.[key] || '';
                const isEditable = lang === 'yo' || lang === 'en';
                return (
                  <Accordion
                    key={key}
                    title={label}
                    subtitle={sub}
                    defaultOpen={isEditable}
                    badge={
                      isEditable
                        ? <StatusPill tone="ember">Editable</StatusPill>
                        : value
                          ? <StatusPill tone="brass">Read only</StatusPill>
                          : <StatusPill tone="moss">Empty</StatusPill>
                    }
                  >
                    {isEditable ? (
                      <Textarea
                        value={value}
                        onChange={e => setDraft({
                          ...draft,
                          transcript: { ...(draft.transcript || {}), [key]: e.target.value },
                        })}
                        rows={14}
                        disabled={contentLoading}
                        placeholder={contentLoading ? 'Loading…' : ''}
                      />
                    ) : (
                      <ReadOnlyText value={value} placeholder="No translation imported yet." />
                    )}
                  </Accordion>
                );
              })}
            </div>

            <div className="md-actions">
              <Button onClick={saveTranscripts} tone="ember" disabled={saving || contentLoading}>
                {saving ? 'Saving…' : 'Save transcripts'}
              </Button>
            </div>
          </Card>
        </div>

        {/* ── Right rail ─────────────────────────────────────────── */}
        <aside className="md-rail">
          <UploadDropzone
            mediaId={id}
            mediaType={(media.type || 'AUDIO') as 'AUDIO' | 'VIDEO'}
            existingFilename={media.file?.filename}
            onComplete={(filename) => {
              setMedia({ ...media, file: { filename } });
              setDraft(d => ({ ...d, file: { filename } }));
            }}
          />

          <Card>
            <h3 className="md-h-mono">ID &amp; timestamps</h3>
            <dl className="md-static md-static--rail">
              <Row label="ID"          value={<code className="md-mono">{media.id}</code>} />
              <Row label="Author"      value={media.author || '—'} />
              <Row label="Creator"     value={media.creatorName || media.creatorId || '—'} />
              <Row label="Created by"  value={media.createdBy || '—'} />
              <Row label="Created"     value={media.createdTimestamp || '—'} />
              <Row label="Updated"     value={media.lastModifiedTimestamp || '—'} />
              <Row label="Published"   value={media.publishTimestamp || '—'} />
              <Row label="File"        value={media.file?.filename ? <code className="md-mono">{media.file.filename}</code> : '—'} />
            </dl>
          </Card>
        </aside>
      </div>

      <ConfirmDialog
        open={deleting}
        title="Delete media"
        message="This removes the media entry and its associated assets. The action is recorded in the audit log."
        requireReason
        destructive
        confirmLabel="Delete"
        onClose={() => setDeleting(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="md-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function MediaStatusPill({ status }: { status?: string }) {
  if (status === 'PUBLISHED')     return <StatusPill tone="moss">Published</StatusPill>;
  if (status === 'UNPUBLISHED')   return <StatusPill tone="ember">Unpublished</StatusPill>;
  if (status === 'FILE_UPLOADED') return <StatusPill tone="brass">Ready</StatusPill>;
  if (status === 'DELETED')       return <StatusPill tone="rust">Deleted</StatusPill>;
  return <StatusPill tone="brass">New</StatusPill>;
}

function ContentBadge({ loading, error }: { loading: boolean; error: string | null }) {
  if (loading) return <StatusPill tone="brass">Loading…</StatusPill>;
  if (error)   return <StatusPill tone="rust">Error</StatusPill>;
  return <StatusPill tone="moss">Loaded</StatusPill>;
}
